
import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { TranslationText } from '@/components/i18n/TranslationText';
import { TranslationButton } from '@/components/i18n/TranslationButton';
import { Check } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { SubscriptionPlan, SubscriptionFeatures } from '@/lib/types';
import { useSystemSettings } from '@/hooks/useSystemSettings';

interface PlanDisplay extends Omit<SubscriptionPlan, 'features'> {
  features: string[];
  popular: boolean;
  price: string;
  buttonText: string;
}

export function PricingSection() {
  const { t } = useTranslation();
  const { getPublicBooleanSetting } = useSystemSettings();
  const [subscriptionsEnabled, setSubscriptionsEnabled] = useState<boolean | null>(null);
  
  // Check if subscriptions are enabled
  useEffect(() => {
    const checkSubscriptionsEnabled = async () => {
      try {
        const enabled = await getPublicBooleanSetting('subscriptions_enabled', true);
        setSubscriptionsEnabled(enabled);
      } catch (error) {
        console.error('Error checking if subscriptions are enabled:', error);
        // Default to showing subscriptions if we can't check
        setSubscriptionsEnabled(true);
      }
    };
    
    checkSubscriptionsEnabled();
  }, [getPublicBooleanSetting]);
  
  const { data: plans = [], isLoading } = useQuery<PlanDisplay[]>({
    queryKey: ['landing-subscription-plans'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .eq('show_on_landing', true)
        .order('monthly_price')
        .limit(4);

      if (error) throw error;
      
      // Transform the data to match the PlanDisplay interface
      return data.map(plan => {
        const planFeatures = plan.features as unknown as SubscriptionFeatures;
        const features = Object.entries(planFeatures)
          .filter(([_, enabled]) => enabled)
          .map(([feature]) => feature);

        return {
          id: plan.id,
          name: plan.name,
          description: plan.description || '',
          plan_type: plan.plan_type,
          monthly_price: plan.monthly_price,
          annual_price: plan.annual_price || 0,
          features,
          is_active: plan.is_active,
          show_on_landing: Boolean(plan.show_on_landing),
          created_at: plan.created_at,
          updated_at: plan.updated_at,
          // Additional display fields
          popular: plan.plan_type === 'premium',
          price: `$${(plan.monthly_price / 100).toFixed(2)}`,
          buttonText: plan.plan_type === 'free' ? 'Get Started' : 'Subscribe Now'
        };
      });
    },
    enabled: subscriptionsEnabled === true  // Only fetch plans if subscriptions are enabled
  });
  
  // If we're still checking subscription status or subscriptions are disabled, don't render the section
  if (subscriptionsEnabled === null) {
    return null; // Loading state
  }
  
  if (subscriptionsEnabled === false) {
    return null; // Don't render anything if subscriptions are disabled
  }
  
  return (
    <section id="pricing" className="py-16 md:py-24">
      <div className="container px-4 md:px-6">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl mb-4">
            <TranslationText i18nKey="pricing.title">
              Simple, Transparent Pricing
            </TranslationText>
          </h2>
          <p className="text-muted-foreground md:text-lg">
            <TranslationText i18nKey="pricing.subtitle">
              Choose the plan that fits your needs
            </TranslationText>
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <Card 
              key={index}
              className={`relative overflow-hidden ${
                plan.popular 
                  ? "border-primary shadow-lg shadow-primary/20" 
                  : "shadow-md"
              }`}
            >
              {plan.popular && (
                <div className="absolute top-0 right-0 bg-primary text-white text-xs py-1 px-3 rounded-bl-lg">
                  <TranslationText i18nKey="pricing.mostPopular">Most Popular</TranslationText>
                </div>
              )}
              <CardHeader>
                <CardTitle className="text-2xl">
                  <TranslationText i18nKey={plan.name}>{plan.name}</TranslationText>
                </CardTitle>
                <div className="mt-2">
                  <span className="text-4xl font-bold">
                    <TranslationText i18nKey={plan.price}>{plan.price}</TranslationText>
                  </span>
                  <span className="text-muted-foreground ml-2">
                    <TranslationText i18nKey="pricing.perMonth">/month</TranslationText>
                  </span>
                </div>
                <CardDescription className="mt-3">
                  <TranslationText i18nKey={plan.description}>{plan.description}</TranslationText>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {Array.isArray(plan.features) && plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <Check className="h-5 w-5 text-green-500 mr-2" />
                      <TranslationText i18nKey={typeof feature === 'string' ? feature : ''}>{typeof feature === 'string' ? feature : ''}</TranslationText>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                <TranslationButton 
                  i18nKey={plan.buttonText}
                  className={`w-full ${plan.popular ? "bg-primary hover:bg-primary/90" : "bg-gray-200 hover:bg-gray-300 dark:bg-gray-800 dark:hover:bg-gray-700 text-foreground"}`}
                />
              </CardFooter>
            </Card>
          ))}
        </div>
        
        <div className="text-center mt-12">
          <p className="text-muted-foreground">
            <TranslationText i18nKey="pricing.question">
              Need a custom solution for your business?
            </TranslationText>{' '}
            <a href="#contact" className="text-primary hover:underline">
              <TranslationText i18nKey="pricing.contactUs">Contact us</TranslationText>
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}
